import {Align, Justify, PositionType} from '@wonderlandengine/react-ui';
import {
    Column,
    Container,
    Image,
    MaterialContext,
    Panel,
    Panel9Slice,
    PanelProps,
    Row,
    Text,
} from '@wonderlandengine/react-ui/components';
import {useContext, useEffect, useMemo, useRef, useState} from 'react';
import {
    FlatOpaqueTextured,
    UIColorTextured,
    UIColorTexturedAlpha,
} from '../../types/materials.js';
import {
    RotBMaterialContextValue,
    useRotBMaterialContext,
} from '../utils/rotb-material-context.js';
import {Emitter, Material} from '@wonderlandengine/api';
import {formatString, insertNewlines, parseColor} from '../utils/utils.js';
import {NotifyPropertyChanged} from '../../utils/NotifyPropertyChanged.js';
import {UiTexturesManager} from '../components/ui-textures-manager.js';
import {MenuButton} from '../components/menu-button.js';
import {colorSwatch} from '../utils/colorSwatch.js';
import {Localization} from '../../utils/Localization.js';
import {goToPreviousMenu} from '../utils/goToPreviousMenu.js';
import {MenuPanels} from '../../models/UIModel.js';
import {WindowHeader} from '../components/window-header.js';
import {goToMenu} from '../utils/goToMenu.js';
import {Statistics} from '../../components/statistics.js';
import {TransactionalNames} from '../../enums/TransactionalNames.js';
import {formatNumber} from '../../utils/Utils.js';
import {StoreManager} from '../../components/store-manager.js';
import {TutorialGameRules} from '../../components/tutorial-game-rules.js';
import {buySkipMission} from '../utils/buySkipMission.js';
import {Sounds} from '../../enums/Sounds.js';

export class SkipMissionButton extends NotifyPropertyChanged {
    private static _instance: SkipMissionButton;
    disabledSkipButton: boolean;
    static get instance(): SkipMissionButton {
        if (!SkipMissionButton._instance) {
            SkipMissionButton._instance = new SkipMissionButton();
        }
        return SkipMissionButton._instance;
    }

    private constructor() {
        super();
    }

    private _isVisible = false;
    get isVisible() {
        return this._isVisible;
    }
    private set isVisible(value: boolean) {
        if (value === this._isVisible) return;
        this._isVisible = value;
        this.notifyPropertyChanged('isVisible');
    }

    private _text: string = '';
    get text() {
        return this._text;
    }
    private set text(value: string) {
        this._text = value;
        this.notifyPropertyChanged('text');
    }

    private _showButton = false;
    get showButton() {
        return this._showButton;
    }
    private set showButton(value: boolean) {
        this._showButton = value;
        this.notifyPropertyChanged('showButton');
    }

    selectedMission: number;
    setMission(missionID: number) {
        this.selectedMission = missionID;

        const storeManager = StoreManager.instance;
        const currentCoins = Statistics.instance.getTransactional(
            TransactionalNames.CoinCount
        );
        const coinCost = storeManager.missionSkipCost;
        if (currentCoins < coinCost) {
            this.text = Localization.instance.get('NeedMoreCoins');
            this.disabledSkipButton = true;
        } else {
            this.disabledSkipButton = false;
            const format = Localization.instance.get('SkipMissionDescriptionFormat');
            this.text = formatString(format, formatNumber(coinCost));
        }
        this.notifyPropertyChanged('disabledSkipButton');
        this.notifyPropertyChanged('text');
    }
}

function useSkipMissionController(): [
    boolean,
    string,
    Material,
    () => void,
    () => void,
    number,
    boolean,
] {
    const controller = useRef(SkipMissionButton.instance);
    const rotbMats = useRotBMaterialContext() as RotBMaterialContextValue;
    //const mats = useContext(MaterialContext);
    const material = rotbMats.panelMaterialTexturedAlpha;
    const backgroundMaterial = useMemo(() => {
        const m = material!.clone() as FlatOpaqueTextured;
        m.setColor(parseColor('#404056EE'));
        m.setFlatTexture(rotbMats?.buttonSimple);
        return m;
    }, []);

    const [isVisible, setIsVisible] = useState(false);
    const [text, setText] = useState(controller.current.text);
    const [disabledSkipButton, setDisabledSkipButton] = useState(false);

    useEffect(() => {
        if (!controller.current) return;
        const propertyChanged = (property: string) => {
            if (property === 'isVisible') {
                setIsVisible(controller.current.isVisible);
            }

            if (property === 'text') {
                setText(controller.current.text);
            }
            if (property === 'disabledSkipButton') {
                setDisabledSkipButton(controller.current.disabledSkipButton);
            }
        };

        controller.current.propertyChanged.add(propertyChanged);

        return () => {
            controller.current.propertyChanged.remove(propertyChanged);
        };
    }, []);

    const [currentCoins, setCurrentCoins] = useState(
        Statistics.instance.getTransactional(TransactionalNames.CoinCount)
    );

    const onCloseClick = () => {
        goToPreviousMenu(MenuPanels.SkipMission);
    };
    const onBuyClick = () => {
        buySkipMission(controller.current.selectedMission);
    };

    return [
        isVisible,
        text,
        backgroundMaterial,
        onCloseClick,
        onBuyClick,
        currentCoins,
        disabledSkipButton,
    ];
}

export const SkipMission = (props: React.PropsWithChildren<PanelProps> & {}) => {
    const rotbMats = useRotBMaterialContext() as RotBMaterialContextValue;
    const mats = useContext(MaterialContext);
    let material = rotbMats?.windowMaterial;

    const bm = useMemo(() => material!.clone(), []);
    (bm as UIColorTextured).setColor(parseColor(colorSwatch.Window));

    const darkBackgroundMaterial = useMemo(() => {
        const m = rotbMats.gradientMaterial!.clone() as UIColorTexturedAlpha;
        m.setColor(parseColor('#000000A0'));
        m.setFlatTexture(UiTexturesManager.instance.whiteSquare);
        return m;
    }, []);

    const shadowMaterial = rotbMats?.windowShadowMaterial;
    const gradientMaterial = rotbMats?.gradientMaterial;
    (gradientMaterial as any).setColor(parseColor(colorSwatch.WindowGradient));

    const [
        isVisible,
        text,
        backgroundMaterial,
        onCloseClick,
        onBuyClick,
        currentCoins,
        disabledSkipButton,
    ] = useSkipMissionController();
    return (
        <Panel
            rounding={0}
            width="100%"
            height="100%"
            justifyContent={Justify.Center}
            alignItems={Align.Center}
            material={darkBackgroundMaterial}
        >
            <Panel9Slice
                width={500}
                height={300}
                borderSize={5}
                material={shadowMaterial}
                borderTextureSize={9 / 64}
            >
                <Panel9Slice
                    left={-4}
                    top={-4}
                    width={500}
                    height={300}
                    borderSize={5}
                    material={bm}
                    borderTextureSize={9 / 64}
                    padding={5}
                >
                    <Panel
                        width="100%"
                        height="100%"
                        rounding={0}
                        material={gradientMaterial}
                    >
                        <Column
                            height="100%"
                            width={450}
                            margin={20}
                            alignSelf={Align.Center}
                            alignItems={Align.Stretch}
                            rowGap={20}
                        >
                            <WindowHeader
                                title={Localization.instance.get('SkipMission')}
                            />
                            <Panel
                                flexGrow={0}
                                rounding={0}
                                material={rotbMats.hudWindowMaterial}
                                height={50}
                            >
                                <Row margin={5} width="100%">
                                    <Image
                                        flexGrow={0}
                                        rounding={0}
                                        width={40}
                                        height={40}
                                        src={UiTexturesManager.instance.coinIconSmall}
                                    ></Image>

                                    <Container marginLeft={8} top={10} flexGrow={1}>
                                        <Text
                                            fontSize={24}
                                            color={colorSwatch.DescriptionText}
                                        >
                                            {formatNumber(currentCoins)}
                                        </Text>
                                    </Container>

                                    <Container
                                        top={-5}
                                        flexGrow={0}
                                        alignSelf={Align.FlexEnd}
                                        justifyContent={Justify.FlexEnd}
                                    >
                                        <MenuButton
                                            width={180}
                                            height={60}
                                            top={-5}
                                            color={colorSwatch.GetCoinsButton}
                                            fontSize={30}
                                            onClick={() => {
                                                goToMenu(MenuPanels.StoreCoins);
                                            }}
                                            text={Localization.instance.get('GetCoins')}
                                        ></MenuButton>
                                    </Container>
                                </Row>
                            </Panel>
                            <Container
                                height={40}
                                justifyContent={
                                    text.length < 41 ? Justify.Center : Justify.FlexStart
                                }
                            >
                                <Text
                                    textAlign="center"
                                    material={rotbMats.regularTextMaterial}
                                    color={colorSwatch.DescriptionText}
                                    fontSize={text.length < 40 ? 26 : 22}
                                    text={insertNewlines(text, 40)}
                                ></Text>
                            </Container>
                            <Row
                                alignSelf={Align.Center}
                                gap={20}
                                alignItems={Align.SpaceEvenly}
                            >
                                <MenuButton
                                    width={200}
                                    height={48}
                                    sound={Sounds.cancel}
                                    color={colorSwatch.CancelButton}
                                    fontSize={24}
                                    onClick={() => onCloseClick()}
                                >
                                    {Localization.instance.get('NeedMoreCoinsDialogReject')}
                                </MenuButton>
                                {!disabledSkipButton && (
                                    <MenuButton
                                        width={200}
                                        height={48}
                                        color={colorSwatch.SpendCoinsButton}
                                        fontSize={24}
                                        icon={UiTexturesManager.instance.coinIconSmall}
                                        onClick={() => onBuyClick()}
                                    >
                                        {formatNumber(
                                            StoreManager.instance.missionSkipCost
                                        )}
                                    </MenuButton>
                                )}
                            </Row>
                        </Column>
                    </Panel>
                </Panel9Slice>
            </Panel9Slice>
        </Panel>
    );
};

SkipMission.displayName = 'SkipMission';
